import { memo } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Loader2, Plus } from "lucide-react";
import { useTranslation } from "@/i18n/useTranslation";
import type { Script } from "@/features/dashboard/types";

interface AddFormCellProps {
  script: Script;
  formId: string;
  displayName: string;
  canLinkForm: boolean;
  isAdding: boolean;
  isGlobalAddDisabled: boolean;
  onAddForm: (script: Script, formId: string) => void;
}

export const AddFormCell = memo(({
  script,
  formId,
  displayName,
  canLinkForm,
  isAdding,
  isGlobalAddDisabled,
  onAddForm,
}: AddFormCellProps) => {
  const { t } = useTranslation();
  const disabled = !canLinkForm || isGlobalAddDisabled;

  return (
    <div className="group relative flex h-full w-full items-center justify-center">
      <Button
        type="button"
        variant="outline"
        size="icon"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          onAddForm(script, formId);
        }}
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-full border-2 border-dashed border-slate-300 bg-white/90 text-slate-500 shadow-2xs transition-all duration-200",
          "hover:border-primary hover:bg-primary/10 hover:text-primary hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
          disabled && "cursor-not-allowed opacity-40 hover:bg-white hover:scale-100 hover:border-slate-300 hover:text-slate-500"
        )}
        aria-label={t("dashboard.table.add_form_aria", {
          form: displayName,
          script: script.movie_name,
        })}
      >
        {isAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
      </Button>
      <span className="absolute top-[calc(50%+28px)] text-xs font-medium text-muted-foreground opacity-0 transition duration-150 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none whitespace-nowrap z-10">
        {t("dashboard.table.add_form_hint")}
      </span>
    </div>
  );
});

AddFormCell.displayName = "AddFormCell";
